import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import { VendorModalService } from 'src/app/services/vendor-modal/vendor-modal.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { GeneralService } from 'src/app/services/general/general.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-vendor-add-edit',
  templateUrl: './vendor-add-edit.component.html',
  styleUrls: ['./vendor-add-edit.component.scss']
})
export class VendorAddEditComponent extends DestructorComponent implements OnInit {

  @Output() vendorAdded = new EventEmitter<void>();
  @Output() vendorUpdated = new EventEmitter<void>();

  // Form properties
  vendorForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedVendor: any = null;

  // File upload properties
  selectedFile: File | null = null;
  fileUploaded: boolean = false;
  uploadedFileUrl: string = '';

  // Dropdown data
  countryList: any[] = [];
  vendorTypes: any[] = [
    { value: 'auction_hall', label: 'Auction Hall' },
    { value: 'deal', label: 'Deal' },
    { value: 'end_user', label: 'End User' },
    { value: 'inspection_agency', label: 'Inspection Agency' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'repair', label: 'Repair' },
    { value: 'seller', label: 'Seller' },
    { value: 'shipping', label: 'Shipping' },
    { value: 'transport_company', label: 'Transport Company' },
    { value: 'yard', label: 'Yard' }
  ];

  constructor(
    private fb: FormBuilder,
    private _vendorService: VendorService,
    private _vendorModalService: VendorModalService,
    private _globalStateService: GlobalStateService,
    private _generalService: GeneralService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadCountries();
    this.setupModalListener();
  }

  // Form getters for validation
  get company_name() { return this.vendorForm.get('company_name'); }
  get vendor_type() { return this.vendorForm.get('vendor_type'); }
  get address_line_1() { return this.vendorForm.get('address_line_1'); }
  get address_line_2() { return this.vendorForm.get('address_line_2'); }
  get city() { return this.vendorForm.get('city'); }
  get state() { return this.vendorForm.get('state'); }
  get country() { return this.vendorForm.get('country'); }
  get zip_code() { return this.vendorForm.get('zip_code'); }
  get email_1() { return this.vendorForm.get('email_1'); }
  get email_2() { return this.vendorForm.get('email_2'); }
  get email_3() { return this.vendorForm.get('email_3'); }
  get contact() { return this.vendorForm.get('contact'); }
  get post() { return this.vendorForm.get('post'); }
  get mobile() { return this.vendorForm.get('mobile'); }
  get tel() { return this.vendorForm.get('tel'); }
  get opening_bal() { return this.vendorForm.get('opening_bal'); }
  get opening_bal_date() { return this.vendorForm.get('opening_bal_date'); }
  get pos_no() { return this.vendorForm.get('pos_no'); }
  get fax_no() { return this.vendorForm.get('fax_no'); }
  get company_tax_code() { return this.vendorForm.get('company_tax_code'); }
  get custom_clearance() { return this.vendorForm.get('custom_clearance'); }
  get type() { return this.vendorForm.get('type'); }

  // Multi-select helper methods
  isVendorTypeSelected(type: string): boolean {
    const selectedTypes = this.vendorForm.get('vendor_type')?.value || [];
    return selectedTypes.includes(type);
  }

  toggleVendorType(type: string): void {
    const currentTypes = this.vendorForm.get('vendor_type')?.value || [];
    let updatedTypes: string[];

    if (currentTypes.includes(type)) {
      // Remove the type
      updatedTypes = currentTypes.filter((t: string) => t !== type);
    } else {
      // Add the type
      updatedTypes = [...currentTypes, type];
    }

    this.vendorForm.patchValue({ vendor_type: updatedTypes });
  }

  getVendorTypeLabel(value: string): string {
    const type = this.vendorTypes.find(t => t.value === value);
    return type ? type.label : value;
  }

  selectAllVendorTypes(): void {
    const allTypes = this.vendorTypes.map(type => type.value);
    this.vendorForm.patchValue({ vendor_type: allTypes });
  }

  clearAllVendorTypes(): void {
    this.vendorForm.patchValue({ vendor_type: [] });
  }

  // Getter for selected vendor types count
  get selectedVendorTypesCount(): number {
    const vendorTypes = this.vendorForm.get('vendor_type')?.value;
    return vendorTypes && Array.isArray(vendorTypes) ? vendorTypes.length : 0;
  }

  // Getter to check if any vendor types are selected
  get hasSelectedVendorTypes(): boolean {
    return this.selectedVendorTypesCount > 0;
  }

  private loadCountries(): void {
    // Subscribe to countries from GlobalStateService
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countryList = countries || [];
      })
    );
  }

  private initializeForm(): void {
    this.vendorForm = this.fb.group({
      company_name: ['', [Validators.required, Validators.minLength(2)]],
      vendor_type: [[], [Validators.required]], // Changed to array for multi-select
      address_line_1: [''],
      address_line_2: [''],
      city: [''],
      state: [''],
      country: [''],
      zip_code: [''],
      email_1: ['', [Validators.required, Validators.email]],
      email_2: ['', [Validators.email]],
      email_3: ['', [Validators.email]],
      contact: [''],
      post: [''],
      mobile: [''],
      tel: [''],
      opening_bal: [''],
      opening_bal_date: [''],
      pos_no: [''],
      fax_no: [''],
      company_tax_code: [''],
      custom_clearance: [''],
      type: ['debit'] // Default to debit
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_vendor_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceVendor = this._vendorModalService.getSelectedVendor();
        if (serviceVendor) {
          this.isEditMode = true;
          this.selectedVendor = serviceVendor;
          this.populateForm(this.selectedVendor);
        } else {
          this.isEditMode = false;
          this.selectedVendor = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._vendorModalService.clearSelectedVendor();
        this.resetForm();
      });
    }
  }

  private populateForm(vendor: any): void {
    if (vendor) {
      this.vendorForm.patchValue({
        company_name: vendor.company_name || '',
        vendor_type: Array.isArray(vendor.vendor_type) ? vendor.vendor_type : (vendor.vendor_type ? [vendor.vendor_type] : []),
        address_line_1: vendor.address_line_1 || '',
        address_line_2: vendor.address_line_2 || '',
        city: vendor.city || '',
        state: vendor.state || '',
        country: vendor.country || '',
        zip_code: vendor.zip_code || '',
        email_1: vendor.email_1 || '',
        email_2: vendor.email_2 || '',
        email_3: vendor.email_3 || '',
        contact: vendor.contact || '',
        post: vendor.post || '',
        mobile: vendor.mobile || '',
        tel: vendor.tel || '',
        opening_bal: vendor.opening_bal || '',
        opening_bal_date: vendor.opening_bal_date || '',
        pos_no: vendor.pos_no || '',
        fax_no: vendor.fax_no || '',
        company_tax_code: vendor.company_tax_code || '',
        custom_clearance: vendor.custom_clearance || '',
        type: vendor.type || 'debit'
      });

      // Set uploaded file URL if exists
      if (vendor.document_url) {
        this.uploadedFileUrl = vendor.document_url;
      }
    }
  }

  private resetForm(): void {
    this.vendorForm.reset();
    this.isLoading = false;
    this.isEditMode = false;
    this.selectedVendor = null;
    this.selectedFile = null;
    this.fileUploaded = false;
    this.uploadedFileUrl = '';

    // Reset file input
    const fileInput = document.getElementById('document') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onSubmit(): void {
    if (this.vendorForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.vendorForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      // Add uploaded file URL if exists
      if (this.uploadedFileUrl) {
        submitData.document_url = this.uploadedFileUrl;
      }

      const apiCall = this.isEditMode
        ? this._vendorService.UpdateData(this.selectedVendor._id, submitData)
        : this._vendorService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Vendor operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Vendor has been successfully updated!"
                : "Vendor has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.vendorUpdated.emit();
                } else {
                  this.vendorAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error saving vendor:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched(this.vendorForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_vendor_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // File upload methods
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.selectedFile = file;
      this.uploadFile(file);
    }
  }

  uploadFile(file: File): void {
    this.fileUploaded = true;
    this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
      next: (res) => {
        const fileUrl = res.data.url;
        const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

        this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
          next: () => {
            this.fileUploaded = false;
            this.uploadedFileUrl = fileUrl;
          },
          error: (error) => {
            this.fileUploaded = false;
            console.error('Error uploading file to S3:', error);
            Swal.fire({
              text: "Error uploading file. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        });
      },
      error: (error) => {
        this.fileUploaded = false;
        console.error('Error getting presigned URL:', error);
        Swal.fire({
          text: "Error uploading file. Please try again.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      }
    });
  }

  removeFile(): void {
    this.selectedFile = null;
    this.uploadedFileUrl = '';
    // Reset file input
    const fileInput = document.getElementById('document') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }
}
